import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerService } from '../../../Services/customer.service';
import { MaintenanceService } from '../../../Services/maintenance.service';
import { Customer } from '../../../Models/customer.model';
import { Maintenance, MaintenanceStatus, MaintenanceRecurrenceType } from '../../../Models/maintenance.model';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { CustomerFormComponent } from '../customer-form/customer-form.component';
import { AuthService } from '../../../api-authorization/authorize.service';

type AgendaFilter = 'attention' | 'today' | 'week' | 'service' | 'all';
type QuickAction = 'plan' | 'service';

interface StatCard {
  label: string;
  value: number;
  icon: string;
  color: string;
  route?: string;
  hint?: string;
}

interface AgendaFilterOption {
  key: AgendaFilter;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-wartung-dashboard',
  templateUrl: './wartung-dashboard.component.html',
  styleUrls: ['./wartung-dashboard.component.css']
})
export class WartungDashboardComponent implements OnInit {
  customers: Customer[] = [];
  allMaintenances: Maintenance[] = [];
  upcomingMaintenances: Maintenance[] = [];
  overdueMaintenances: Maintenance[] = [];
  loading = true;
  customerSearch = '';
  agendaFilter: AgendaFilter = 'attention';
  /** Wenn gesetzt, wartet eine Schnellaktion darauf, dass ein Kunde gewählt wird. */
  pendingAction: QuickAction | null = null;

  readonly agendaFilters: AgendaFilterOption[] = [
    { key: 'attention', label: 'Fokus', icon: 'priority_high' },
    { key: 'today', label: 'Heute', icon: 'today' },
    { key: 'week', label: '7 Tage', icon: 'date_range' },
    { key: 'service', label: 'Einsätze', icon: 'build' },
    { key: 'all', label: 'Alle offen', icon: 'list_alt' }
  ];

  stats: StatCard[] = [];

  constructor(
    private customerService: CustomerService,
    private maintenanceService: MaintenanceService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  /** Montage = nur lesen: Schnellaktionen/Direkt-Buttons ausblenden. */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  /** Startet eine Schnellaktion: der Benutzer wählt anschließend den Kunden. */
  startQuickAction(action: QuickAction): void {
    this.pendingAction = action;
  }

  cancelQuickAction(): void {
    this.pendingAction = null;
  }

  get pendingActionLabel(): string {
    if (this.pendingAction === 'plan') return 'Wartung planen';
    if (this.pendingAction === 'service') return 'Einsatz erfassen';
    return '';
  }

  /** Öffnet die Kundenseite und startet dort direkt die gewählte Aktion. */
  openCustomerWithAction(customer: Customer, action: QuickAction, event?: Event): void {
    event?.stopPropagation();
    this.pendingAction = null;
    this.router.navigate(['/wartung/kunden', customer.id], { queryParams: { action } });
  }

  loadData(): void {
    this.loading = true;
    this.customerService.getCustomers().subscribe({
      next: (customers) => {
        this.customers = customers ?? [];
        this.allMaintenances = this.customers
          .flatMap(c => (c.equipments ?? []).flatMap(e =>
            (e.maintenances ?? []).map(m => ({ ...m, equipment: m.equipment ?? e }))
          ));

        this.upcomingMaintenances = this.allMaintenances
          .filter(m => this.isOpenMaintenance(m))
          .sort((a, b) => this.getTargetDate(a).getTime() - this.getTargetDate(b).getTime())
          .slice(0, 12);

        this.overdueMaintenances = this.allMaintenances
          .filter(m => this.isOverdue(m));

        this.buildStats();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private buildStats(): void {
    const totalEquipment = this.customers.reduce((sum, c) => sum + (c.equipments?.length ?? 0), 0);
    const activeCount = this.allMaintenances.filter(m => this.isOpenMaintenance(m)).length;
    const completedCount = this.allMaintenances.filter(m => m.status === MaintenanceStatus.Completed).length;
    const weekCount = this.allMaintenances.filter(m => this.isOpenMaintenance(m) && this.getDaysUntil(m) >= 0 && this.getDaysUntil(m) <= 7).length;

    this.stats = [
      { label: 'Kunden', value: this.customers.length, icon: 'business', color: '#1976d2', route: '/wartung/kunden', hint: 'zur Auswahl' },
      { label: 'Anlagen', value: totalEquipment, icon: 'electrical_services', color: '#6a1b9a', hint: 'bei Kunden' },
      { label: 'Nächste 7 Tage', value: weekCount, icon: 'date_range', color: '#ef6c00', hint: 'fällig' },
      { label: 'Überfällig', value: this.overdueMaintenances.length, icon: 'warning', color: '#d32f2f', hint: 'prüfen' },
      { label: 'Erledigt', value: completedCount, icon: 'check_circle', color: '#2e7d32', hint: 'gesamt' },
    ];
  }

  get filteredAgendaMaintenances(): Maintenance[] {
    const openItems = this.allMaintenances
      .filter(m => this.isOpenMaintenance(m))
      .sort((a, b) => this.getTargetDate(a).getTime() - this.getTargetDate(b).getTime());

    switch (this.agendaFilter) {
      case 'attention':
        return openItems.filter(m => this.isOverdue(m) || this.isDueSoon(m) || m.status === MaintenanceStatus.InProgress).slice(0, 12);
      case 'today':
        return openItems.filter(m => this.getDaysUntil(m) === 0);
      case 'week':
        return openItems.filter(m => this.getDaysUntil(m) >= 0 && this.getDaysUntil(m) <= 7);
      case 'service':
        return openItems.filter(m => m.recurrenceType === MaintenanceRecurrenceType.None);
      case 'all':
      default:
        return openItems.slice(0, 20);
    }
  }

  get visibleCustomers(): Customer[] {
    const term = this.customerSearch.toLowerCase().trim();
    return [...this.customers]
      .filter(c => {
        if (!term) return true;
        const equipmentText = (c.equipments ?? [])
          .map(e => `${e.name} ${e.location ?? ''}`)
          .join(' ');
        return `${c.name} ${c.address ?? ''} ${c.contactPersonName ?? ''} ${equipmentText}`
          .toLowerCase()
          .includes(term);
      })
      .sort((a, b) => {
        const rankDiff = this.getCustomerRank(a) - this.getCustomerRank(b);
        if (rankDiff !== 0) return rankDiff;
        const aDate = this.getNextMaintenance(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bDate = this.getNextMaintenance(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (aDate !== bDate) return aDate - bDate;
        return a.name.localeCompare(b.name, 'de');
      })
      .slice(0, 8);
  }

  getAgendaFilterCount(filter: AgendaFilter): number {
    const openItems = this.allMaintenances.filter(m => this.isOpenMaintenance(m));

    switch (filter) {
      case 'attention':
        return openItems.filter(m => this.isOverdue(m) || this.isDueSoon(m) || m.status === MaintenanceStatus.InProgress).length;
      case 'today':
        return openItems.filter(m => this.getDaysUntil(m) === 0).length;
      case 'week':
        return openItems.filter(m => this.getDaysUntil(m) >= 0 && this.getDaysUntil(m) <= 7).length;
      case 'service':
        return openItems.filter(m => m.recurrenceType === MaintenanceRecurrenceType.None).length;
      case 'all':
      default:
        return openItems.length;
    }
  }

  getStatusClass(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Planned: return 'status-planned';
      case MaintenanceStatus.InProgress: return 'status-progress';
      case MaintenanceStatus.Completed: return 'status-completed';
      case MaintenanceStatus.Overdue: return 'status-overdue';
      case MaintenanceStatus.Cancelled: return 'status-cancelled';
      default: return '';
    }
  }

  getStatusLabel(status: MaintenanceStatus, m?: Maintenance): string {
    const isEinsatz = m && m.recurrenceType === MaintenanceRecurrenceType.None;
    if (isEinsatz) {
      switch (status) {
        case MaintenanceStatus.Planned: return 'Aktiv';
        case MaintenanceStatus.InProgress: return 'Aktiv';
        case MaintenanceStatus.Completed: return 'Abgeschlossen';
        case MaintenanceStatus.Overdue: return 'Überfällig';
        case MaintenanceStatus.Cancelled: return 'Storniert';
        default: return status;
      }
    }
    switch (status) {
      case MaintenanceStatus.Planned: return 'Geplant';
      case MaintenanceStatus.InProgress: return 'In Arbeit';
      case MaintenanceStatus.Completed: return 'Erledigt';
      case MaintenanceStatus.Overdue: return 'Überfällig';
      case MaintenanceStatus.Cancelled: return 'Storniert';
      default: return status;
    }
  }

  deleteMaintenance(m: Maintenance, event: Event): void {
    event.stopPropagation();
    const isEinsatz = m.recurrenceType === MaintenanceRecurrenceType.None;
    const typeLabel = isEinsatz ? 'Einsatz' : 'Wartung';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: `${typeLabel} löschen`, message: `"${m.title}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r) {
        this.maintenanceService.deleteMaintenance(m.id).subscribe({
          next: () => {
            try { localStorage.removeItem(`service-call-${m.id}`); } catch { /* ignore */ }
            this.snackBar.open(`${typeLabel} gelöscht`, 'OK', { duration: 3000 });
            this.loadData();
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 3000 })
        });
      }
    });
  }

  openNewCustomerForm(): void {
    const dialogRef = this.dialog.open(CustomerFormComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: null,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Kunde angelegt', 'OK', { duration: 3000 });
        this.loadData();
      }
    });
  }

  getCustomerForMaintenance(m: Maintenance): string {
    for (const c of this.customers) {
      for (const e of c.equipments ?? []) {
        if (e.id === m.equipmentId) return c.name;
      }
    }
    return '–';
  }

  getEquipmentForMaintenance(m: Maintenance): string {
    return m.equipment?.name ?? '–';
  }

  getTechnicianNames(m: Maintenance): string {
    if (!m.assignedUsers?.length) return '–';
    return m.assignedUsers.map(u => u.userName).join(', ');
  }

  navigateToCustomer(m: Maintenance): void {
    for (const c of this.customers) {
      for (const e of c.equipments ?? []) {
        if (e.id === m.equipmentId) {
          this.router.navigate(['/wartung/kunden', c.id]);
          return;
        }
      }
    }
  }

  navigateToMaintenance(m: Maintenance): void {
    this.router.navigate(['/wartung/auftrag', m.id]);
  }

  navigateTo(route?: string): void {
    if (route) this.router.navigate([route]);
  }

  openCustomer(customer: Customer): void {
    // Wenn eine Schnellaktion aktiv ist, diese direkt auf der Kundenseite starten.
    if (this.pendingAction) {
      this.openCustomerWithAction(customer, this.pendingAction);
      return;
    }
    this.router.navigate(['/wartung/kunden', customer.id]);
  }

  isOverdue(m: Maintenance): boolean {
    if (m.status === MaintenanceStatus.Overdue) return true;
    const due = this.getTargetDate(m);
    return due < new Date() && m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Cancelled;
  }

  isDueSoon(m: Maintenance): boolean {
    const days = this.getDaysUntil(m);
    return days >= 0 && days <= 7;
  }

  getMaintenanceTypeLabel(m: Maintenance): string {
    return m.recurrenceType === MaintenanceRecurrenceType.None ? 'Einsatz' : 'Wartung';
  }

  getDueText(m: Maintenance): string {
    const days = this.getDaysUntil(m);
    if (days < -1) return `seit ${Math.abs(days)} Tagen`;
    if (days === -1) return 'seit gestern';
    if (days === 0) return 'heute';
    if (days === 1) return 'morgen';
    if (days <= 7) return `in ${days} Tagen`;
    return this.getTargetDate(m).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  getCustomerInitial(customer: Customer): string {
    return customer.name?.charAt(0)?.toUpperCase() ?? '?';
  }

  getCustomerStatusClass(customer: Customer): string {
    const status = this.getCustomerStatus(customer);
    return `customer-${status}`;
  }

  getCustomerStatusLabel(customer: Customer): string {
    const status = this.getCustomerStatus(customer);
    if (status === 'danger') return `${this.getOverdueCount(customer)} überfällig`;
    if (status === 'warn') return 'bald fällig';
    if ((customer.equipments?.length ?? 0) === 0) return 'Setup offen';
    return 'OK';
  }

  getCustomerSummary(customer: Customer): string {
    const equipmentCount = customer.equipments?.length ?? 0;
    const activeCount = this.getActiveCount(customer);
    return `${equipmentCount} Anlagen · ${activeCount} offen`;
  }

  getNextMaintenanceLabel(customer: Customer): string {
    const next = this.getNextMaintenance(customer);
    if (!next) return 'kein Termin';
    const days = this.getDaysUntilDate(next);
    if (days < 0) return 'überfällig';
    if (days === 0) return 'heute';
    if (days === 1) return 'morgen';
    return next.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  getEquipmentPreview(customer: Customer): string {
    const names = (customer.equipments ?? []).map(e => e.name).filter(Boolean);
    if (names.length === 0) return 'Noch keine Anlage';
    const preview = names.slice(0, 2).join(', ');
    return names.length > 2 ? `${preview} +${names.length - 2}` : preview;
  }

  getActiveCount(customer: Customer): number {
    return (customer.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isOpenMaintenance(m))
      .length;
  }

  getOverdueCount(customer: Customer): number {
    return (customer.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isOverdue(m))
      .length;
  }

  getNextMaintenance(customer: Customer): Date | null {
    const dates = (customer.equipments ?? [])
      .flatMap(e => e.maintenances ?? [])
      .filter(m => this.isOpenMaintenance(m))
      .map(m => this.getTargetDate(m))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    return dates[0] ?? null;
  }

  getCustomerStatus(customer: Customer): 'ok' | 'warn' | 'danger' | 'empty' {
    if (this.getOverdueCount(customer) > 0) return 'danger';
    if ((customer.equipments?.length ?? 0) === 0) return 'empty';
    const next = this.getNextMaintenance(customer);
    if (next && this.getDaysUntilDate(next) <= 7 && this.getDaysUntilDate(next) >= 0) return 'warn';
    return 'ok';
  }

  trackByMaintenanceId(_: number, maintenance: Maintenance): number {
    return maintenance.id;
  }

  trackByCustomerId(_: number, customer: Customer): number {
    return customer.id;
  }

  private isOpenMaintenance(m: Maintenance): boolean {
    return m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Cancelled;
  }

  getTargetDate(m: Maintenance): Date {
    const raw = m.dueDate ?? m.nextScheduledDate ?? m.scheduledDate;
    return new Date(raw);
  }

  private getDaysUntil(m: Maintenance): number {
    return this.getDaysUntilDate(this.getTargetDate(m));
  }

  private getDaysUntilDate(date: Date): number {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    return Math.round((target - startOfToday) / 86400000);
  }

  private getCustomerRank(customer: Customer): number {
    const status = this.getCustomerStatus(customer);
    if (status === 'danger') return 0;
    if (status === 'warn') return 1;
    if (status === 'empty') return 2;
    return 3;
  }
}
